import { XCloseIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { Button as AriaButton, Toolbar } from 'react-aria-components';

interface BulkAction {
  /**
   * Stable key for the action.
   *
   * @required
   */
  key: string;
  /**
   * The button label.
   *
   * @required
   */
  label: string;
  /**
   * The icon before the label, sized 15px; it takes the button's text color.
   *
   * @required
   */
  icon: React.ReactNode;
  /**
   * Runs the action.
   *
   * @required
   */
  onPress: () => void;
  /**
   * Whether the action is unavailable for the current selection.
   *
   * @optional
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Tooltip explaining the action, e.g. "Compare 3 assets side by side".
   *
   * @optional
   */
  title?: string;
  /**
   * Tooltip shown instead of `title` while disabled, explaining why, e.g. "Select at least two assets to compare".
   *
   * @optional
   */
  disabledReason?: string;
  /**
   * `danger` turns the button red on hover, for destructive actions like Delete. It stays neutral at rest.
   *
   * @optional
   * @default 'default'
   */
  tone?: 'default' | 'danger';
}

interface BulkActionBarProps {
  /**
   * How many items are selected. The bar renders only while this is above zero.
   *
   * @required
   */
  count: number;
  /**
   * The actions, in display order.
   *
   * @required
   */
  actions: BulkAction[];
  /**
   * Clears the selection; called by the × button and by Esc.
   *
   * @required
   */
  onClear: () => void;
  /**
   * Accessible name for the toolbar.
   *
   * @optional
   * @default 'Bulk actions'
   */
  'aria-label'?: string;
  /**
   * @optional
   */
  className?: string;
}

/** Inputs the user types into. Checkboxes and sliders aren't here: Esc from a tile checkbox should clear. */
const TEXT_INPUT_TYPES = new Set(['text', 'search', 'email', 'url', 'tel', 'password', 'number']);

/** Whether a key event comes from somewhere the user is typing, where Esc belongs to the field. */
function isEditableTarget(target: EventTarget | null) {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable ||
      target.tagName === 'TEXTAREA' ||
      (target instanceof HTMLInputElement && TEXT_INPUT_TYPES.has(target.type)))
  );
}

/** Shared by the action and clear buttons: square, no border, white focus outline inside the button. */
const buttonBaseClasses = cx(
  'h-16 cursor-pointer outline-none',
  'data-[focus-visible]:outline-2 data-[focus-visible]:outline data-[focus-visible]:outline-white',
  'data-[focus-visible]:-outline-offset-2',
);

function BulkActionButton(props: Omit<BulkAction, 'key'>) {
  const { label, icon, onPress, isDisabled = false, title, disabledReason, tone = 'default' } = props;
  const isDanger = tone === 'danger';
  const reasonId = React.useId();
  const showReason = isDisabled && Boolean(disabledReason);

  // The tooltip sits on a wrapper: disabled buttons get no pointer events in some browsers, so a title on the button
  // itself would never show its disabled reason
  return (
    <span className="inline-flex" title={isDisabled ? (disabledReason ?? title) : title}>
      <AriaButton
        onPress={onPress}
        isDisabled={isDisabled}
        aria-describedby={showReason ? reasonId : undefined}
        className={cx(
          buttonBaseClasses,
          'inline-flex items-center gap-3 px-6 text-sm font-semibold whitespace-nowrap',
          'bg-transparent text-primary-on-brand',
          // Hover is instant; danger only turns red on hover, never at rest
          isDanger
            ? 'data-[hovered]:bg-error-600 data-[pressed]:bg-error-700'
            : 'data-[hovered]:bg-white/[.12] data-[pressed]:bg-white/[.2]',
          'data-[disabled]:cursor-not-allowed data-[disabled]:bg-transparent data-[disabled]:text-primary-on-brand/40',
        )}
      >
        <span className="flex shrink-0" aria-hidden="true">
          {icon}
        </span>
        {label}
      </AriaButton>
      {/* The title tooltip is mouse-only; screen readers get the reason from the button's description */}
      {showReason && (
        <span id={reasonId} className="sr-only">
          {disabledReason}
        </span>
      )}
    </span>
  );
}

function Divider() {
  return <div aria-hidden="true" className="mx-3 h-9 w-px shrink-0 bg-white/[.18]" />;
}

/**
 * Floating toolbar for acting on a multi-selection (assets in a collection or the library): the selected count, the
 * actions, and a button to clear the selection. Fixed at the bottom center of the viewport, it slides up when the
 * selection starts and renders nothing while the selection is empty. Esc clears the selection.
 *
 * @example
 * <BulkActionBar
 *   count={selected.size}
 *   onClear={clearSelection}
 *   actions={[
 *     { key: 'fav', label: 'Favorite', icon: <HeartIcon size={15} />, onPress: favoriteSelected },
 *     { key: 'delete', label: 'Delete', icon: <Trash01Icon size={15} />, onPress: confirmDelete, tone: 'danger' },
 *   ]}
 * />
 */
function BulkActionBar(props: BulkActionBarProps) {
  const { count, actions, onClear, 'aria-label': ariaLabel = 'Bulk actions', className } = props;
  const isVisible = count > 0;

  React.useEffect(() => {
    if (!isVisible) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      // Overlays (like a delete confirmation) handle their own Esc and stop it, and Esc in a text field backs out of
      // the field; leave both alone
      if (event.key === 'Escape' && !event.defaultPrevented && !isEditableTarget(event.target)) {
        onClear();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, onClear]);

  // TODO(BLU-348): when the bar unmounts with focus inside it (× via keyboard, or Delete confirmed), focus falls to
  // body. Decide where it goes, either an optional focus target here or on the consumer side
  if (!isVisible) {
    return null;
  }

  return (
    <Toolbar
      aria-label={ariaLabel}
      className={cx(
        // z-40: above page content, below the dialog backdrop (z-50) so dialogs like the delete confirmation cover it
        'fixed bottom-12 left-1/2 z-40 flex -translate-x-1/2 items-center gap-4 py-4 pr-4 pl-8',
        // On narrow screens the bar stays inside the viewport and the actions scroll, keeping × in reach
        'max-w-[calc(100vw-32px)]',
        'bg-foreground-primary text-primary-on-brand shadow-xl',
        'motion-safe:animate-bulk-bar-up',
        className,
      )}
    >
      <span className="shrink-0 whitespace-nowrap text-sm font-semibold">{count} selected</span>
      <Divider />
      <div className="flex min-w-0 items-center gap-4 overflow-x-auto [scrollbar-width:none]">
        {actions.map(({ key, ...action }) => (
          <BulkActionButton key={key} {...action} />
        ))}
      </div>
      <Divider />
      <span className="inline-flex shrink-0" title="Clear selection (Esc)">
        <AriaButton
          onPress={onClear}
          aria-label="Clear selection"
          className={cx(
            buttonBaseClasses,
            'grid w-16 place-items-center bg-transparent text-primary-on-brand/75',
            'data-[hovered]:bg-white/[.12] data-[pressed]:bg-white/[.2]',
          )}
        >
          <XCloseIcon size={14} aria-hidden="true" />
        </AriaButton>
      </span>
    </Toolbar>
  );
}

export { BulkActionBar };
export type { BulkAction, BulkActionBarProps };
